import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red button, for deleting or anything that can't be undone */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * In-app confirmation popup, used instead of the browser's confirm() box:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete this?", destructive: true }))) return;
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    // A new question answers any one still open with "no"
    resolver.current?.(false);
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOptions(null);
  };

  const Icon = options?.destructive ? AlertTriangle : HelpCircle;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!options} onOpenChange={(open) => !open && close(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div
                className={
                  options?.destructive
                    ? "size-9 shrink-0 rounded-full bg-destructive/10 text-destructive flex items-center justify-center"
                    : "size-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center"
                }
              >
                <Icon className="size-5" />
              </div>
              <div className="space-y-1.5 pt-1 text-left">
                <DialogTitle className="text-base">{options?.title}</DialogTitle>
                {options?.message && <DialogDescription className="text-sm">{options.message}</DialogDescription>}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => close(false)}>
              {options?.cancelLabel || "Cancel"}
            </Button>
            <Button variant={options?.destructive ? "destructive" : "default"} onClick={() => close(true)} autoFocus>
              {options?.confirmLabel || "OK"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return fn;
}
